import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getFaqById, createFaq, updateFaq, getFaqCategories } from '../../services/faqsService';
import { useToast } from '../../contexts/ToastContext';
import Loader from '../../components/common/Loader';
import Input from '../../components/common/Input';
import RichTextEditor from '../../components/forms/RichTextEditor';
import Button from '../../components/common/Button';

function FaqFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError, showWarning } = useToast();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    question: '', 
    answer: '', 
    category: '', 
    display_order: 0, 
    is_active: true
  });
  const [categories, setCategories] = useState([]);
  
  // Validation states
  const [validationErrors, setValidationErrors] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);

  // Validation functions
  const validateField = (name, value) => {
    let error = '';
    
    switch (name) {
      case 'question':
        if (!value || !value.trim()) {
          error = 'Question is required';
        } else if (value.trim().length < 10) {
          error = 'Question must be at least 10 characters';
        } else if (value.trim().length > 500) {
          error = 'Question must be less than 500 characters';
        }
        break;
        
      case 'answer':
        if (!value || !value.trim()) {
          error = 'Answer is required';
        } else if (value.trim().length < 20) {
          error = 'Answer must be at least 20 characters';
        } else if (value.trim().length > 5000) {
          error = 'Answer must be less than 5000 characters';
        }
        break;
        
      case 'category':
        if (!value || !value.trim()) {
          error = 'Category is required';
        } else if (value.trim().length < 2) {
          error = 'Category must be at least 2 characters';
        } else if (value.trim().length > 100) {
          error = 'Category must be less than 100 characters';
        }
        break;
        
      case 'display_order':
        if (value < 0) {
          error = 'Display order cannot be negative';
        } else if (value > 999) {
          error = 'Display order must be less than 1000';
        }
        break;
        
      default:
        break;
    }
    
    return error;
  };

  // Validation message component
  const ValidationMessage = ({ error, type = 'error' }) => {
    if (!error) return null;
    
    const styles = {
      error: 'bg-gradient-to-r from-red-50 to-pink-50 border-red-200 text-red-700',
      warning: 'bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200 text-amber-700',
      success: 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200 text-green-700'
    };
    
    const icons = {
      error: '❌',
      warning: '⚠️',
      success: '✅'
    };
    
    return (
      <div className={`${styles[type]} border px-3 py-2 rounded-lg text-sm font-medium mt-2 shadow-sm animate-fade-in`}>
        <div className="flex items-center">
          <span className="mr-2">{icons[type]}</span>
          {error}
        </div>
      </div>
    );
  };

  // Check form validity
  const checkFormValidity = (errors, data) => {
    const hasErrors = Object.values(errors).some(err => err !== '');
    const hasRequiredFields = data.question.trim() && data.answer.trim() && data.category.trim();
    setIsFormValid(!hasErrors && hasRequiredFields);
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        // Fetch categories first
        const categoryData = await getFaqCategories();
        setCategories(Array.isArray(categoryData) ? categoryData : []);
        
        // Fetch FAQ data if editing
        if (id) {
          const faq = await getFaqById(id);
          console.log('📝 FAQ data loaded for editing:', faq);
          setFormData({
            question: faq.question || '',
            answer: faq.answer || '',
            category: faq.category || '',
            display_order: faq.display_order || 0,
            is_active: faq.is_active !== undefined ? faq.is_active : true
          });
        }
      } catch (err) {
        const errorMessage = 'Failed to load data: ' + err.message;
        setError(errorMessage);
        showError(errorMessage);
        console.error('❌ FAQ form data fetch error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, showError]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: newValue,
    }));
    
    // Real-time validation for non-checkbox fields
    if (type !== 'checkbox') {
      const error = validateField(name, newValue);
      setValidationErrors(prev => ({
        ...prev,
        [name]: error
      }));
      
      // Check form validity
      const newFormData = { ...formData, [name]: newValue };
      const newErrors = { ...validationErrors, [name]: error };
      checkFormValidity(newErrors, newFormData);
    }
  };

  const handleRichTextChange = (value) => {
    setFormData(prev => ({
      ...prev,
      answer: value
    }));
    
    // Validate answer field
    const error = validateField('answer', value);
    setValidationErrors(prev => ({
      ...prev,
      answer: error
    }));
    
    // Check form validity
    const newFormData = { ...formData, answer: value };
    const newErrors = { ...validationErrors, answer: error };
    checkFormValidity(newErrors, newFormData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validate all fields before submission
    const errors = {};
    Object.keys(formData).forEach(key => {
      if (key !== 'is_active') { // Skip validation for checkbox
        const error = validateField(key, formData[key]);
        if (error) errors[key] = error;
      }
    });
    
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      showWarning('Please fix all validation errors before submitting');
      return;
    }
    
    // Client-side validation
    if (!formData.question.trim()) {
      showWarning('Please enter a question');
      return;
    }
    
    if (!formData.answer.trim()) {
      showWarning('Please enter an answer');
      return;
    }
    
    if (!formData.category.trim()) {
      showWarning('Please select or enter a category');
      return;
    }
    
    setLoading(true);
    try {
      console.log('📤 Submitting FAQ data:', formData);
      
      if (id) {
        await updateFaq(id, formData);
        showSuccess('FAQ updated successfully!');
      } else {
        await createFaq(formData);
        showSuccess('FAQ created successfully!');
      }
      navigate('/faqs');
    } catch (err) {
      const errorMessage = 'Failed to save FAQ: ' + err.message;
      setError(errorMessage);
      showError(errorMessage);
      console.error('❌ FAQ save error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md relative m-6 shadow-md" role="alert">
        <span className="block sm:inline">{error}</span>
      </div>
    );
  }

  return (
    <div className="p-2 bg-gray-50 min-h-screen">
      <div className="w-full">
        <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center px-2">
          <div className="w-3 h-3 bg-purple-500 rounded-full mr-3"></div>
          {id ? 'Edit FAQ' : 'Create New FAQ'}
        </h2>

      <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-lg p-6 space-y-6 border border-gray-200 hover:shadow-xl transition-shadow duration-300 mx-2">
        <div className="space-y-6">
        {/* Question Field */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <div className="w-3 h-3 bg-blue-500 rounded-full mr-3"></div>
            Question
          </h3>
          <Input 
            label="FAQ Question" 
            id="question" 
            name="question" 
            value={formData.question} 
            onChange={handleInputChange} 
            required 
            placeholder="Enter the FAQ question (min 10 characters)"
            className={`${validationErrors.question ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-blue-200 focus:border-blue-500 focus:ring-blue-500'}`}
          />
          <ValidationMessage error={validationErrors.question} />
          {formData.question && !validationErrors.question && formData.question.trim().length >= 10 && (
            <ValidationMessage error={`Great question! (${formData.question.trim().length} characters)`} type="success" />
          )}
          {formData.question && formData.question.trim().length > 0 && formData.question.trim().length < 10 && (
            <ValidationMessage error={`${10 - formData.question.trim().length} more characters needed`} type="warning" />
          )}
        </div>

        {/* Answer Field */}
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
            Answer
          </h3>
          <RichTextEditor 
            label="Detailed Answer" 
            value={formData.answer} 
            onChange={handleRichTextChange} 
            required 
            placeholder="Enter the detailed answer (min 20 characters)..."
            className={validationErrors.answer ? 'border-red-300' : 'border-green-200'}
          />
          <ValidationMessage error={validationErrors.answer} />
          {formData.answer && !validationErrors.answer && formData.answer.trim().length >= 20 && (
            <ValidationMessage error={`Perfect answer! (${formData.answer.trim().length} characters)`} type="success" />
          )}
          {formData.answer && formData.answer.trim().length > 0 && formData.answer.trim().length < 20 && (
            <ValidationMessage error={`${20 - formData.answer.trim().length} more characters needed`} type="warning" />
          )}
        </div>

        {/* Category Selection */}
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <div className="w-3 h-3 bg-purple-500 rounded-full mr-3"></div>
            Category
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-700 text-sm font-semibold mb-2">
                Existing Category
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                className={`shadow-sm border rounded-lg w-full py-3 px-4 text-gray-700 focus:outline-none focus:ring-2 transition-all duration-200 ${
                  validationErrors.category 
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
                    : 'border-purple-200 focus:border-purple-500 focus:ring-purple-500'
                }`}
              >
                <option value="">Select existing category</option>
                {categories.map((cat) => (
                  <option key={cat.category} value={cat.category}>
                    {cat.category} ({cat.faq_count} FAQs)
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-semibold mb-2">
                Or Create New Category
              </label>
              <input
                type="text"
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                placeholder="Enter new category name"
                className={`shadow-sm border rounded-lg w-full py-3 px-4 text-gray-700 focus:outline-none focus:ring-2 transition-all duration-200 ${
                  validationErrors.category 
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
                    : 'border-purple-200 focus:border-purple-500 focus:ring-purple-500'
                }`}
              />
            </div>
          </div>
          <ValidationMessage error={validationErrors.category} />
          {formData.category && !validationErrors.category && formData.category.trim().length >= 2 && (
            <ValidationMessage 
              error={categories.find(cat => cat.category === formData.category) 
                ? `Using existing category: ${formData.category}` 
                : `Creating new category: ${formData.category}`} 
              type="success" 
            />
          )}
          <p className="text-sm text-gray-600 mt-3 bg-white px-3 py-2 rounded-lg border border-purple-200">
            <span className="font-medium">💡 Tip:</span> Choose an existing category from the dropdown or type a new category name in the text field
          </p>
        </div>

        {/* Display Order and Status Row */}
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-6 border border-amber-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <div className="w-3 h-3 bg-amber-500 rounded-full mr-3"></div>
            Settings & Status
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Input 
                label="Display Order" 
                id="display_order" 
                name="display_order" 
                type="number" 
                value={formData.display_order} 
                onChange={handleInputChange}
                min="0"
                max="999"
                placeholder="0"
                helperText="Lower numbers appear first"
                className={`${validationErrors.display_order ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-amber-200 focus:border-amber-500 focus:ring-amber-500'}`}
              />
              <ValidationMessage error={validationErrors.display_order} />
              {formData.display_order >= 0 && formData.display_order <= 999 && !validationErrors.display_order && (
                <ValidationMessage 
                  error={`Display order: ${formData.display_order} ${formData.display_order === 0 ? '(appears first)' : ''}`} 
                  type="success" 
                />
              )}
            </div>
            
            <div className="flex items-center bg-white p-4 rounded-lg border border-amber-200 shadow-sm hover:bg-amber-50 transition-colors duration-200">
              <input 
                type="checkbox" 
                id="is_active" 
                name="is_active" 
                checked={formData.is_active} 
                onChange={handleInputChange} 
                className="h-5 w-5 text-amber-600 focus:ring-amber-500 border-gray-300 rounded" 
              />
              <label htmlFor="is_active" className="ml-3 block text-sm text-gray-900">
                <div className="flex items-center">
                  <span className="font-medium">Active Status</span>
                  {formData.is_active && (
                    <div className="w-2 h-2 bg-green-500 rounded-full ml-2"></div>
                  )}
                </div>
                <span className="text-gray-600 block text-xs mt-1">
                  {formData.is_active ? 'FAQ will be visible to users' : 'FAQ will be hidden from users'}
                </span>
              </label>
            </div>
          </div>
        </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-4 pt-6 border-t-2 border-gray-200">
          <Button 
            type="button" 
            onClick={() => navigate('/faqs')} 
            variant="secondary"
            disabled={loading}
            className="py-3 px-6 text-sm font-medium rounded-lg hover:shadow-md transition-all duration-200"
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="primary"
            disabled={loading || !isFormValid || Object.values(validationErrors).some(err => err !== '')}
            className={`min-w-32 py-3 px-6 text-sm font-medium rounded-lg hover:shadow-md transition-all duration-200 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 ${
              (!isFormValid || Object.values(validationErrors).some(err => err !== '')) 
                ? 'opacity-50 cursor-not-allowed' 
                : ''
            }`}
          >
            {loading ? 'Saving...' : (id ? 'Update FAQ' : 'Create FAQ')}
          </Button>
        </div>
        
        {/* Form Validation Summary */}
        {!isFormValid && (formData.question || formData.answer || formData.category) && (
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-4 mt-4">
            <div className="flex items-center">
              <span className="mr-2">⚠️</span>
              <div>
                <p className="text-amber-800 font-medium text-sm">Form Incomplete</p>
                <p className="text-amber-700 text-xs mt-1">
                  Please complete all required fields and fix any validation errors before submitting.
                </p>
              </div>
            </div>
          </div>
        )}
      </form>
      </div>
    </div>
  );
}

export default FaqFormPage;